import {
  Component,
  computed,
  HostBinding,
  inject,
  signal,
  WritableSignal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  DataScope,
  DefaultDataProcessingFeatures,
  DefaultDataScope,
  IQBOOST_V2_FEATURE_FLAG,
  IQBoostViewStore,
  IQDataProcessingFeatures,
  isGlobalServersInEffect,
  ViewType,
  AiFeaturesService,
} from '@maximizer/ai/data-access';
import { FeatureService } from '@maximizer/core/shared/data-access';
import {
  CDN_URL,
  Octopus,
  TranslationListItem,
} from '@maximizer/core/shared/domain';
import { DisposableComponent } from '@maximizer/core/shared/ui/components/base';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import { cloneObject } from '@maximizer/shared/util';
import { TranslatePipe } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_DROPDOWNLIST } from '@progress/kendo-angular-dropdowns';
import { KENDO_INPUTS } from '@progress/kendo-angular-inputs';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import { forkJoin, map, Observable } from 'rxjs';
import {
  EventAction,
  EventJourney,
  IQBoostEventTracker,
} from '../classes/event-tracker';
import { IQBoostTextVersion } from '../classes/iqboost';
import {
  PROCESSING_FEATURE_KEYS,
  ProcessingFeatureKey,
} from './models/processing-feature-key.model';

@Component({
  selector: 'ai-iqboost-privacy-security-center',
  templateUrl: './iqboost-privacy-security-center.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    KENDO_BUTTON,
    KENDO_DROPDOWNLIST,
    KENDO_INPUTS,
    KENDO_LABEL,
    UI_KENDO,
    TranslatePipe,
  ],
})
export class IqboostPrivacySecurityCenterComponent extends DisposableComponent {
  readonly store = inject(IQBoostViewStore);
  readonly tracker = inject(IQBoostEventTracker);
  private readonly feature = inject(FeatureService);
  private readonly aiFeatures = inject(AiFeaturesService);
  readonly cdn = inject(CDN_URL);

  @HostBinding('class') hostClasses = 'ai-ng';
  aiClass = true;

  protected readonly isFFGlobalModelsEnabled = this.feature.isFeatureOn(
    'core-ai-global-models-enabled',
    true,
  );
  protected readonly isFFOnlineResourcesEnabled = this.feature.isFeatureOn(
    'core-ai-online-resources-enabled',
    true,
  );
  dataScope: WritableSignal<DataScope> = signal(DefaultDataScope);
  dataProcessingFeatures: WritableSignal<IQDataProcessingFeatures> = signal(
    DefaultDataProcessingFeatures,
  );
  private readonly openedProcessingFeatures: IQDataProcessingFeatures;

  dueDates = signal([] as TranslationListItem<string>[]);
  selectedDueDate?: TranslationListItem<string>;

  textVersion = computed(() =>
    this.aiFeatures.isSLE()
      ? IQBoostTextVersion.defaultVersion
      : IQBoostTextVersion.faVersion,
  );
  isAdmin = computed(() => this.store.dataProcessingFeatureGroup().isAdmin);
  isGlobalModelsEnabledByAdmin = computed(
    () =>
      this.store.dataProcessingFeatureGroup().admin.dataProcessingFeatures
        .useGlobalServers,
  );
  isOnlineResourcesEnabledByAdmin = computed(
    () =>
      this.store.dataProcessingFeatureGroup().admin.dataProcessingFeatures
        .useWebData,
  );
  protected readonly isGlobalModelsLocked = computed(
    () =>
      !this.isFFGlobalModelsEnabled ||
      (!this.isAdmin() && !this.isGlobalModelsEnabledByAdmin()),
  );
  protected readonly isOnlineResourcesLocked = computed(
    () =>
      !this.isFFOnlineResourcesEnabled ||
      (!this.isAdmin() && !this.isOnlineResourcesEnabledByAdmin()),
  );

  constructor() {
    super();

    const openedProcessingFeatures = this.initialProcessingFeatures();
    this.openedProcessingFeatures = cloneObject(openedProcessingFeatures);
    this.dataProcessingFeatures.set(cloneObject(openedProcessingFeatures));

    this.dataScope.set(cloneObject(this.store.dataScope()));
    this.loadDueDate();
  }

  private initialProcessingFeatures(): IQDataProcessingFeatures {
    const adminProcessingFeatures =
      this.store.dataProcessingFeatureGroup().admin.dataProcessingFeatures;

    if (this.isAdmin()) {
      return adminProcessingFeatures;
    }

    const userProcessingFeatures =
      this.store.dataProcessingFeatureGroup().user.dataProcessingFeatures;
    return {
      ...userProcessingFeatures,
      anonymized:
        userProcessingFeatures.anonymized || adminProcessingFeatures.anonymized,
      useGlobalServers: isGlobalServersInEffect(
        adminProcessingFeatures,
        userProcessingFeatures.useGlobalServers,
        this.feature.isFeatureOn(IQBOOST_V2_FEATURE_FLAG, false),
      ),
      useWebData:
        userProcessingFeatures.useWebData && adminProcessingFeatures.useWebData,
    };
  }

  private processedDataScope(): DataScope {
    return {
      ...this.dataScope(),
      dataFiltering: {
        dueDate: this.selectedDueDate?.id as Octopus.DateOptionId,
      },
    };
  }

  private isLocked(key: ProcessingFeatureKey): boolean {
    return key === 'useGlobalServers'
      ? this.isGlobalModelsLocked()
      : this.isOnlineResourcesLocked();
  }

  private choicesDifferentFrom(
    reference: IQDataProcessingFeatures,
    keys: readonly ProcessingFeatureKey[] = PROCESSING_FEATURE_KEYS,
  ): Partial<IQDataProcessingFeatures> {
    const shown = this.dataProcessingFeatures();
    return Object.fromEntries(
      keys
        .filter((key) => shown[key] !== reference[key])
        .map((key) => [key, shown[key]]),
    );
  }

  private changedChoices(): Partial<IQDataProcessingFeatures> {
    const unlockedKeys = PROCESSING_FEATURE_KEYS.filter(
      (key) => !this.isLocked(key),
    );
    return this.choicesDifferentFrom(
      this.openedProcessingFeatures,
      unlockedKeys,
    );
  }

  private userRowChange(
    changedChoices: Partial<IQDataProcessingFeatures>,
  ): Partial<IQDataProcessingFeatures> {
    return this.isAdmin()
      ? this.choicesDifferentFrom(
          this.store.dataProcessingFeatureGroup().user.dataProcessingFeatures,
        )
      : changedChoices;
  }

  private saveFiltersConfigurationUser(
    dataScope: DataScope,
    processingChange: Partial<IQDataProcessingFeatures>,
  ): Observable<boolean> {
    return this.aiFeatures.updateUserIqBoostRecord({
      wasFirstTimeOpenedPrivacySecurityCenter: true,
      dataScope,
      ...(Object.keys(processingChange).length > 0
        ? { dataProcessingFeatures: processingChange }
        : {}),
    });
  }

  private saveSettings(
    dataScope: DataScope,
    changedChoices: Partial<IQDataProcessingFeatures>,
  ): Observable<boolean> {
    const saveUser = this.saveFiltersConfigurationUser(
      dataScope,
      this.userRowChange(changedChoices),
    );

    if (!this.isAdmin() || Object.keys(changedChoices).length === 0) {
      return saveUser;
    }

    return forkJoin([
      saveUser,
      this.aiFeatures.setOrganizationProcessingFeatures(changedChoices),
    ]).pipe(map(([userSaved, adminSaved]) => userSaved && adminSaved));
  }

  private finishSave(
    saved: boolean,
    dataScope: DataScope,
    isDataScopeDifferent: boolean,
  ): void {
    if (!saved) {
      this.store.setLoading(false);
      this.store.setShowErrorMessageDialog(true);
      return;
    }

    this.store.setDataScope(dataScope);
    if (isDataScopeDifferent) {
      this.store.triggerScopeChange({
        useAllHouseholds: this.store.useAllHouseholds(),
      });
    } else {
      this.store.setLoading(false);
    }
    this.store.setViewType(ViewType.Report);
  }

  private loadDueDate(): void {
    const currentDueDate =
      this.store.dataScope().dataFiltering?.dueDate ??
      Octopus.DateOptionId.LASTYEAR;

    this.dueDates.set(
      Octopus.getDateOptions([
        Octopus.DateOptionId.THISWEEK,
        Octopus.DateOptionId.LASTWEEK,
        Octopus.DateOptionId.THISMONTH,
        Octopus.DateOptionId.THISANDLASTMONTH,
        Octopus.DateOptionId.LASTMONTH,
        Octopus.DateOptionId.LASTTHREEMONTHS,
        Octopus.DateOptionId.LASTSIXMONTHS,
        Octopus.DateOptionId.LASTTWELVEMONTHS,
        Octopus.DateOptionId.THISANDLAST2YEARS,
        Octopus.DateOptionId.THISANDLAST3YEARS,
        Octopus.DateOptionId.THISANDLAST4YEARS,
        Octopus.DateOptionId.THISANDLAST5YEARS,
      ]),
    );

    this.selectedDueDate = Octopus.DateOptions[currentDueDate];
  }

  private isDataScopeDifferent(a: DataScope, b: DataScope): boolean {
    return (
      a.all !== b.all ||
      a.householdDetails !== b.householdDetails ||
      a.accountDetails !== b.accountDetails ||
      a.notes !== b.notes ||
      a.emails !== b.emails ||
      a.documents !== b.documents ||
      a.calls !== b.calls ||
      a.allInteractions !== b.allInteractions ||
      a.dataFiltering?.dueDate !==
        (this.selectedDueDate?.id as Octopus.DateOptionId)
    );
  }

  closePrivacySecurityCenter(): void {
    this.tracker.trackEvent(EventJourney.SecurityCenter, EventAction.Saved);

    const changedChoices = this.changedChoices();
    const isProcessingFeaturesDifferent =
      Object.keys(changedChoices).length > 0;
    const isDataScopeDifferent = this.isDataScopeDifferent(
      this.dataScope(),
      this.store.dataScope(),
    );

    if (
      isDataScopeDifferent ||
      isProcessingFeaturesDifferent ||
      !this.store.dataProcessingFeatureGroup().user
        .wasFirstTimeOpenedPrivacySecurityCenter
    ) {
      this.store.setLoading(true);
      const dataScope = this.processedDataScope();

      this.saveSettings(dataScope, changedChoices).subscribe((saved) =>
        this.finishSave(saved, dataScope, isDataScopeDifferent),
      );
    } else {
      this.store.setViewType(ViewType.Report);
    }
  }
}
